import { compareVersions, type Release, type ReleaseAsset } from '@nexo/types';
import { useQuery } from '@tanstack/react-query';
import type { Update } from '@tauri-apps/plugin-updater';
import { Download, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { isDesktop, openExternal } from '../lib/platform';

const DISMISS_KEY = 'nexo.update.dismissed';
const CHECK_INTERVAL = 6 * 60 * 60 * 1000;

type SelfUpdate =
  | { phase: 'idle' }
  | { phase: 'downloading'; update: Update; progress: number | null }
  | { phase: 'ready'; update: Update }
  | { phase: 'installing'; update: Update };

/** How the desktop app was installed (`install_kind` command in apps/desktop). */
interface InstallInfo {
  kind: 'appimage' | 'snap' | 'deb' | 'rpm' | 'bundle' | 'other';
  arch: string;
}

/** Progress of a .deb/.rpm update installed from the app through the system package manager. */
type PackageUpdate =
  | { phase: 'idle' }
  | { phase: 'downloading'; progress: number | null }
  | { phase: 'installing' }
  | { phase: 'done' }
  | { phase: 'error'; message: string | null };

type PackageEvent =
  { event: 'progress'; data: { received: number; total: number | null } } | { event: 'installing' };

/**
 * Desktop update notices. Installs that can update themselves (Windows, macOS, AppImage) download
 * the signed update in the background and offer to restart; mandatory updates install straight
 * away. System packages (.deb/.rpm) update on demand: the app downloads and verifies the package
 * and the system package manager installs it after asking for the admin password. Anything else
 * (Snap, which its store updates) links to the download page.
 */
export function UpdateBanner() {
  const { t } = useTranslation();
  const selfUpdate = useSelfUpdate();
  const latest = useQuery({
    queryKey: ['releases', 'latest'],
    queryFn: () => api.releases.latest(),
    enabled: isDesktop,
    refetchInterval: CHECK_INTERVAL,
  });
  const install = useQuery({
    queryKey: ['desktop', 'install-kind'],
    queryFn: async () => {
      const { invoke } = await import('@tauri-apps/api/core');
      return invoke<InstallInfo>('install_kind');
    },
    enabled: isDesktop,
    staleTime: Infinity,
  });
  const [packageUpdate, updatePackage] = usePackageUpdate();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY));

  if (!isDesktop) return null;

  if (selfUpdate.phase !== 'idle') {
    const { version } = selfUpdate.update;
    return (
      <Bar tone="highlight">
        <RefreshCw
          className={`size-4 shrink-0 ${selfUpdate.phase === 'ready' ? '' : 'animate-spin'}`}
        />
        <span className="flex-1">
          {selfUpdate.phase === 'downloading' &&
            (selfUpdate.progress === null
              ? t('Descargando Nexo {{version}}…', { version })
              : t('Descargando Nexo {{version}} ({{progress}}%)', {
                  version,
                  progress: selfUpdate.progress,
                }))}
          {selfUpdate.phase === 'ready' && (
            <Trans
              i18nKey="<b>Nexo {{version}}</b> está listo. Reinicia para usar la nueva versión."
              values={{ version }}
              components={{ b: <strong /> }}
            />
          )}
          {selfUpdate.phase === 'installing' && t('Instalando Nexo {{version}}…', { version })}
        </span>
        {selfUpdate.phase === 'ready' && (
          <BarButton onClick={() => void installAndRestart(selfUpdate.update)}>
            {t('Reiniciar ahora')}
          </BarButton>
        )}
      </Bar>
    );
  }

  const release = latest.data;
  if (!release || compareVersions(release.version, __APP_VERSION__) <= 0) return null;
  const busy = packageUpdate.phase !== 'idle' && packageUpdate.phase !== 'error';
  if (!busy && !release.mandatory && dismissed === release.version) return null;

  const asset = install.data && systemPackage(release, install.data);
  const tone = release.mandatory || packageUpdate.phase === 'error' ? 'danger' : 'highlight';
  const downloadPage = (
    <BarButton onClick={() => void openExternal('https://nexo-hub.dev/download')}>
      {t('Descargar')}
    </BarButton>
  );
  // Mandatory versions can't be hidden: they still need the user (and, for packages, a password).
  const dismiss = !release.mandatory && (
    <button
      type="button"
      aria-label={t('Ocultar')}
      onClick={() => {
        localStorage.setItem(DISMISS_KEY, release.version);
        setDismissed(release.version);
        updatePackage(null);
      }}
      className="rounded-md p-1 hover:bg-black/15"
    >
      <X className="size-4" />
    </button>
  );

  if (asset && packageUpdate.phase !== 'idle') {
    const { version } = release;
    return (
      <Bar tone={tone}>
        {packageUpdate.phase === 'done' ? (
          <RefreshCw className="size-4 shrink-0" />
        ) : (
          <Download
            className={`size-4 shrink-0 ${packageUpdate.phase === 'error' ? '' : 'animate-pulse'}`}
          />
        )}
        <span className="flex-1">
          {packageUpdate.phase === 'downloading' &&
            (packageUpdate.progress === null
              ? t('Descargando Nexo {{version}}…', { version })
              : t('Descargando Nexo {{version}} ({{progress}}%)', {
                  version,
                  progress: packageUpdate.progress,
                }))}
          {packageUpdate.phase === 'installing' &&
            t(
              'Instalando Nexo {{version}}… Escribe tu contraseña de administrador en la ventana del sistema.',
              { version },
            )}
          {packageUpdate.phase === 'done' && (
            <Trans
              i18nKey="<b>Nexo {{version}}</b> está instalado. Reinicia para usar la nueva versión."
              values={{ version }}
              components={{ b: <strong /> }}
            />
          )}
          {packageUpdate.phase === 'error' && (
            <Trans
              i18nKey="No se pudo actualizar a <b>Nexo {{version}}</b>: {{message}} Puedes reintentarlo o descargarla desde la web."
              values={{ version, message: packageUpdate.message ?? t('Error inesperado.') }}
              components={{ b: <strong /> }}
            />
          )}
        </span>
        {packageUpdate.phase === 'done' && (
          <BarButton onClick={() => void relaunchApp()}>{t('Reiniciar')}</BarButton>
        )}
        {packageUpdate.phase === 'error' && (
          <>
            <BarButton onClick={() => updatePackage(release, asset)}>{t('Reintentar')}</BarButton>
            {downloadPage}
            {dismiss}
          </>
        )}
      </Bar>
    );
  }

  return (
    <Bar tone={tone}>
      <Download className="size-4 shrink-0" />
      <span className="flex-1">
        {release.mandatory ? (
          <Trans
            i18nKey="Actualización obligatoria: <b>Nexo {{version}}</b> (tienes la {{current}})."
            values={{ version: release.version, current: __APP_VERSION__ }}
            components={{ b: <strong /> }}
          />
        ) : (
          <Trans
            i18nKey="Nueva versión disponible: <b>Nexo {{version}}</b> (tienes la {{current}})."
            values={{ version: release.version, current: __APP_VERSION__ }}
            components={{ b: <strong /> }}
          />
        )}
        {asset && ` ${t('El sistema te pedirá la contraseña de administrador para instalarla.')}`}
      </span>
      {asset ? (
        <BarButton onClick={() => updatePackage(release, asset)}>{t('Actualizar ahora')}</BarButton>
      ) : (
        downloadPage
      )}
      {dismiss}
    </Bar>
  );
}

function Bar({ tone, children }: { tone: 'highlight' | 'danger'; children: React.ReactNode }) {
  return (
    <div
      className={`flex items-center gap-3 px-4 py-2 text-sm ${tone === 'danger' ? 'bg-danger text-white' : 'bg-highlight text-highlight-fg'}`}
    >
      {children}
    </div>
  );
}

function BarButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md bg-black/15 px-2.5 py-1 font-medium hover:bg-black/25"
    >
      {children}
    </button>
  );
}

async function installAndRestart(update: Update): Promise<void> {
  await update.install();
  await relaunchApp();
}

async function relaunchApp(): Promise<void> {
  const { relaunch } = await import('@tauri-apps/plugin-process');
  await relaunch();
}

/** The signed .deb/.rpm of `release` for this install, when it updates through its package. */
function systemPackage(release: Release, install: InstallInfo): ReleaseAsset | undefined {
  if (install.kind !== 'deb' && install.kind !== 'rpm') return undefined;
  return release.assets.find(
    (a) =>
      a.os === 'linux' &&
      a.kind === install.kind &&
      (a.arch === install.arch || a.arch === 'universal') &&
      a.signature,
  );
}

/**
 * Downloads, verifies and installs a .deb/.rpm through the desktop shell (`install_system_package`,
 * which runs the package manager with pkexec: the system asks for the admin password).
 */
function usePackageUpdate(): [
  PackageUpdate,
  (release: Release | null, asset?: ReleaseAsset) => void,
] {
  const [state, setState] = useState<PackageUpdate>({ phase: 'idle' });

  /** Starts updating to `release`, or clears a failed attempt with `null`. */
  const start = useCallback((release: Release | null, asset?: ReleaseAsset) => {
    if (!release || !asset) {
      setState({ phase: 'idle' });
      return;
    }
    const run = async () => {
      setState({ phase: 'downloading', progress: null });
      try {
        const { Channel, invoke } = await import('@tauri-apps/api/core');
        const onEvent = new Channel<PackageEvent>();
        onEvent.onmessage = (message) => {
          if (message.event === 'installing') setState({ phase: 'installing' });
          else {
            const { received, total } = message.data;
            setState({
              phase: 'downloading',
              progress: total ? Math.min(100, Math.round((received / total) * 100)) : null,
            });
          }
        };
        await invoke('install_system_package', {
          url: asset.url,
          signature: asset.signature,
          version: release.version,
          onEvent,
        });
        setState({ phase: 'done' });
      } catch (error) {
        console.warn('[updater]', error);
        setState({
          phase: 'error',
          message: typeof error === 'string' ? error : null,
        });
      }
    };
    void run();
  }, []);

  return [state, start];
}

/** Periodically checks the signed update feed and downloads new versions in the background. */
function useSelfUpdate(): SelfUpdate {
  const [state, setState] = useState<SelfUpdate>({ phase: 'idle' });

  useEffect(() => {
    if (!isDesktop) return;
    let alive = true;
    // A function so TypeScript doesn't narrow the flag across awaits.
    const isAlive = () => alive;
    let busy = false;

    const run = async () => {
      if (busy) return;
      busy = true;
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        if (!(await invoke<boolean>('self_update_supported'))) return;
        const { check } = await import('@tauri-apps/plugin-updater');
        const update = await check();
        if (!update || !isAlive()) return;

        let total: number | null = null;
        let received = 0;
        setState({ phase: 'downloading', update, progress: null });
        await update.download((event) => {
          if (event.event === 'Started') total = event.data.contentLength ?? null;
          if (event.event === 'Progress') {
            received += event.data.chunkLength;
            if (total && isAlive()) {
              setState({
                phase: 'downloading',
                update,
                progress: Math.min(100, Math.round((received / total) * 100)),
              });
            }
          }
        });
        if (!isAlive()) return;
        const release = await api.releases.latest().catch(() => null);
        if (release?.version === update.version && release.mandatory) {
          setState({ phase: 'installing', update });
          await installAndRestart(update);
          return;
        }
        setState({ phase: 'ready', update });
        clearInterval(timer);
      } catch (error) {
        // Offline or feed unavailable: the download-page banner still covers the update.
        console.warn('[updater]', error);
        if (isAlive()) setState({ phase: 'idle' });
      } finally {
        busy = false;
      }
    };

    const timer = setInterval(() => void run(), CHECK_INTERVAL);
    void run();
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  return state;
}
